import React, { useEffect, useState, useRef } from "react";
import { Row, Col, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHeart as faHeartSolid,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { faHeart as faHeartRegular } from "@fortawesome/free-regular-svg-icons";
import { useLocation, useParams } from "react-router-dom";
import {
  createAuthenticatedAxiosInstance,
  fetchPublicProductById,
  toggleFavorite,
  getProductReviews,
} from "api/auth";
import { PublicProductDetails, SizeDetail } from "interfaces/IProduct";
import { IReviewDetailResponse } from "interfaces/IReviews";
import LoadingScreen from "components/general/LoadingScreen";
import CustomNavbarClients from "../navBarClients/CustomNavbarClients";
import ClientMenuBar from "../ClientMenuBar";
import { useAuthStore } from "../../../../zustand/loginStore";
import { useCartStore } from "../../../../zustand/cartStore";
import { useUserStore } from "../../../../zustand/userStore";
import "./ProductDetailsClients.scss";
import Footer from "../Footer";
import NewestProducts from "../newestProducts/NewestProducts";
import RelatedProducts from "../relatedProducts/RelatedProducts";
import ProductReviews from "./ProductReviews";
import { useBreakpoints } from "hooks/useBreakPoints";
import { primaryColor } from "utils/colors";
import stars from "assets/estrellitas.png";
import ShareIcon from "components/icons/ShareIcon";
import CustomToast from "components/general/CustomToast";
import ImageModal from "components/general/ImageModal";
import { isProductFavorite } from "helpers/favorites";

const ProductDetailsClients: React.FC = () => {
  const { favoriteProducts, setFavoriteProducts } = useUserStore();
  const { id } = useParams();
  const location = useLocation();
  const [product, setProduct] = useState<PublicProductDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedColorIndex, setSelectedColorIndex] = useState(0);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [selectedSize, setSelectedSize] = useState<SizeDetail>();
  const [selectedSizeId, setSelectedSizeId] = useState<number>(0);
  const [priceInfo, setPriceInfo] = useState<JSX.Element>(<></>);
  const [isDescriptionOpen, setIsDescriptionOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [startIndex, setStartIndex] = useState(0);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipOpacity, setTooltipOpacity] = useState(1);
  const [showToast, setShowToast] = useState(false);
  const [showCartToast, setShowCartToast] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [favoriteToast, setFavoriteToast] = useState<{
    message: string;
    show: boolean;
    success: boolean;
  }>({ message: "", show: false, success: true });
  const [reviews, setReviews] = useState<IReviewDetailResponse[]>([]);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const addToCart = useCartStore((state) => state.addItem);
  const { token } = useAuthStore();
  const axiosInstance = createAuthenticatedAxiosInstance(token);
  const { isMobile, isTablet } = useBreakpoints();
  const [heartDisabled, setHeartdisabled] = useState<boolean>(false);
  const reviewsRef = useRef<HTMLDivElement>(null);

  /**
   * modular component to show discount quantities in case the product has discount
   */
  const handleSizeChange = (product?: SizeDetail) => {
    if (!product) return <></>;
    const hasDiscount = !!product.discount_type;
    const isPercentOff = product.discount_type.includes("%");

    const price = (
      <>
        {hasDiscount ? (
          <div className="d-flex gap-2 align-content-baseline">
            <span className="price">${product.new_price || 0}</span>
            {isPercentOff ? (
              <span className="discount-quantity-details">
                -{Math.trunc(product.discount)}
                {isPercentOff && "%"}
              </span>
            ) : (
              <span className="discount-quantity-details">
                - ${product.discount}
              </span>
            )}
            <span className="before-discount">${product.price || 0}</span>
          </div>
        ) : (
          <span className="price">${product.price || 0}</span>
        )}
      </>
    );
    setSelectedSize(product);
    setPriceInfo(price);
  };

  const maxVisibleThumbnails = 4;
  const totalImages = product?.colors[selectedColorIndex]?.images?.length ?? 0;

  const handleScrollUp = () => {
    setStartIndex((prev) => Math.max(0, prev - 1));
  };

  const handleScrollDown = () => {
    setStartIndex((prev) =>
      Math.min(totalImages - maxVisibleThumbnails, prev + 1)
    );
  };

  const handleOpenImageModal = () => {
    setIsImageModalOpen(true);
  };

  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
  };

  // Función para hacer scroll hacia las reseñas
  const scrollToReviews = () => {
    if (reviewsRef.current) {
      reviewsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Función para renderizar las estrellas
  const renderStars = (rating: number) => {
    return (
      <div className="product-stars">
        {[1, 2, 3, 4, 5].map((star) => {
          const starValue = rating - (star - 1);
          let starClass = "product-star";

          if (starValue >= 1) {
            starClass += " active";
          } else if (starValue > 0) {
            starClass += " half-active";
          }

          return (
            <span key={star} className={starClass}>
              ★
            </span>
          );
        })}
      </div>
    );
  };

  // Función para formatear el rating
  const formatRating = (rating: number) => {
    // Si el número es entero, mostrar sin decimales
    if (Number.isInteger(rating)) {
      return rating.toString();
    }
    // Si tiene decimales, mostrar con 1 decimal
    return rating.toFixed(1);
  };

  // Función para manejar plural/singular de opinión
  const formatOpinions = (count: number) => {
    return count === 1 ? "opinión" : "opiniones";
  };

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const productData = await fetchPublicProductById(Number(id));
        setProduct(productData);
        if (productData?.colors[0]?.sizes[0]?.size_id) {
          setSelectedSizeId(productData.colors[0].sizes[0].size_id);
          handleSizeChange(productData.colors[0].sizes[0]);
        }
      } catch (error) {
        console.error("Error loading product:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProduct();
  }, [id]);

  /**
   * effect for favorites to find them in the store
   */
  useEffect(() => {
    setIsFavorite(false);
    favoriteProducts?.forEach((fav) => {
      if (id === fav.product_id.toString()) {
        setIsFavorite(true);
        return;
      }
    });
  }, [favoriteProducts, id]);

  // useEffect separado para cargar las reviews
  useEffect(() => {
    const loadReviews = async () => {
      // Limpiar reviews anteriores
      setReviews([]);
      setAverageRating(0);
      setTotalReviews(0);

      try {
        const reviewsData = await getProductReviews(Number(id));
        setReviews(reviewsData);

        if (reviewsData.length > 0) {
          const totalRating = reviewsData.reduce(
            (sum, review) => sum + review.qualification,
            0
          );
          const average = totalRating / reviewsData.length;
          setAverageRating(average);
          setTotalReviews(reviewsData.length);
        }
      } catch (error) {
        console.error("Error loading reviews:", error);
      }
    };

    if (id) {
      loadReviews();
    }
  }, [id]);

  /**
   * use effect used to change the price element
   * [selectedSizeId] The current selected size id
   */
  useEffect(() => {
    const selection = product?.colors[selectedColorIndex]?.sizes?.find(
      (x) => selectedSizeId === x.size_id
    );
    handleSizeChange(selection);
  }, [selectedSizeId, selectedColorIndex]);

  const handleColorChange = (index: number) => {
    setSelectedColorIndex(index);
    setSelectedImageIndex(0);
    if (product?.colors[index]?.sizes[0]?.size_id) {
      setSelectedSize(product.colors[index].sizes[0]);
    }
  };

  const handleShareClick = () => {
    // Get the full URL of the current page
    const productUrl = window.location.origin + location.pathname;

    // Copy to clipboard
    navigator.clipboard
      .writeText(productUrl)
      .then(() => {
        // Show success toast
        setShowToast(true);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  const handleHeartClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowTooltip(true);
    setTooltipOpacity(1);

    setTimeout(() => {
      setTooltipOpacity(0);
      setTimeout(() => {
        setShowTooltip(false);
      }, 1000);
    }, 1500);
  };

  /**
   * Calls the api that toggle the favorite function and adds it or removes it from the wishlist
   */
  const handleFavoriteClick = async (
    e: React.MouseEvent,
    productId: number
  ) => {
    e.stopPropagation();
    if (token) {
      setHeartdisabled(true);
      const { data, success } = await toggleFavorite(axiosInstance, productId);
      if (!success) {
        setHeartdisabled(false);
        setFavoriteToast({
          message: `El producto no pudo ser ${
            isFavorite ? "removido de" : "agregado a"
          } a la lista de favoritos, por favor intente nuevamente.`,
          show: true,
          success: false,
        });
        return;
      }
      data && setFavoriteProducts(data);
      setFavoriteToast({
        message: `El producto fue ${
          isFavorite ? "removido de" : "agregado a"
        } la lista de favoritos.`,
        show: true,
        success: true,
      });
      setIsFavorite(!isFavorite);
      setHeartdisabled(false);
    }
  };

  const favoriteTooltip = (
    <Tooltip
      id="favorite-tooltip"
      className="custom-tooltip-client"
      style={{
        opacity: tooltipOpacity,
        lineHeight: "1.3",
        transition: "opacity 0.5s ease-in-out",
      }}
    >
      Inicia sesión o crea
      <br />
      una cuenta para
      <br />
      guardar tus artículos
      <br />
      favoritos!
    </Tooltip>
  );

  if (isLoading || !product) {
    return <LoadingScreen loadingText="Cargando producto..." />;
  }

  return (
    <>
      <CustomNavbarClients />
      <ClientMenuBar />
      <div className="product-details-container">
        <Row style={{ width: "100%" }}>
          {(isTablet || isMobile) && (
            <Col lg={12} className="mobile-header">
              <div className="product-header">
                <h2>{product.name}</h2>{" "}
                <Button className="share-button" onClick={handleShareClick}>
                  <ShareIcon size={30} color="#614900" />
                </Button>
              </div>
              <div className="price">
                ${product?.colors[selectedColorIndex]?.sizes[0]?.price || 0}
              </div>
            </Col>
          )}
          <Col lg={1} className="thumbnail-column">
            {startIndex > 0 && (
              <button className="scroll-button" onClick={handleScrollUp}>
                <FontAwesomeIcon icon={faChevronUp} />
              </button>
            )}

            {product?.colors[selectedColorIndex]?.images
              .slice(startIndex, startIndex + maxVisibleThumbnails)
              .map((img, index) => (
                <div
                  key={startIndex + index}
                  className={`thumbnail-wrapper ${
                    selectedImageIndex === startIndex + index ? "selected" : ""
                  }`}
                  onClick={() => setSelectedImageIndex(startIndex + index)}
                >
                  <img
                    src={img}
                    alt={`${product.name}-${startIndex + index}`}
                  />
                </div>
              ))}

            {startIndex + maxVisibleThumbnails <
              product?.colors[selectedColorIndex]?.images?.length && (
              <button className="scroll-button" onClick={handleScrollDown}>
                <FontAwesomeIcon icon={faChevronDown} />
              </button>
            )}
          </Col>
          <Col lg={5} className="main-image-column">
            <div className="main-image-wrapper" onClick={handleOpenImageModal}>
              <div className="image-background">
                <img
                  src={
                    product?.colors[selectedColorIndex]?.images[
                      selectedImageIndex
                    ]
                  }
                  alt={product.name}
                />
              </div>
            </div>
          </Col>
          <Col lg={5} className="product-details">
            <div className="product-header">
              <h2>{product.name}</h2>
              <Button
                className="share-button"
                style={{ marginTop: "0.3rem" }}
                onClick={handleShareClick}
              >
                <ShareIcon size={40} color="#614900" />
              </Button>
            </div>
            {/* Calificaciones del producto */}
            {totalReviews > 0 && (
              <div
                className="product-rating-section clickable"
                onClick={scrollToReviews}
              >
                {renderStars(averageRating)}
                <span className="rating-text">
                  {formatRating(averageRating)} ({totalReviews}{" "}
                  {formatOpinions(totalReviews)})
                </span>
              </div>
            )}
            {priceInfo}
            <hr />
            <div className="colors-section">
              <label>
                Estilos disponibles:{" "}
                {product?.colors[selectedColorIndex]?.colorName}
              </label>
              <div className="color-options">
                {product?.colors.map((color, index) => (
                  <button
                    key={index}
                    className={`color-circle ${
                      selectedColorIndex === index ? "selected" : ""
                    }`}
                    style={{ backgroundColor: color.color }}
                    onClick={() => handleColorChange(index)}
                  />
                ))}
              </div>
            </div>
            <div className="sizes-section">
              <label>Tallas:</label>
              <div className="size-options">
                {product?.colors[selectedColorIndex]?.sizes.map((size) => (
                  <button
                    key={size.size_id}
                    className={`size-button ${
                      selectedSizeId === size.size_id ? "selected" : ""
                    }`}
                    onClick={() => setSelectedSizeId(size.size_id)}
                  >
                    {size.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="actions">
              {token ? (
                <button
                  disabled={heartDisabled}
                  className="like-button"
                  onClick={(e) => handleFavoriteClick(e, product.id)}
                >
                  <FontAwesomeIcon
                    icon={isFavorite ? faHeartSolid : faHeartRegular}
                    style={{ height: "40px" }}
                  />
                </button>
              ) : (
                <OverlayTrigger
                  show={showTooltip}
                  trigger="click"
                  placement="top"
                  overlay={favoriteTooltip}
                  rootClose
                >
                  <button className="like-button" onClick={handleHeartClick}>
                    <FontAwesomeIcon
                      icon={faHeartRegular}
                      style={{ height: "40px" }}
                    />
                  </button>
                </OverlayTrigger>
              )}
              <button
                className="add-cart-button"
                onClick={() => {
                  if (selectedSize) {
                    const result = addToCart(
                      product,
                      selectedColorIndex,
                      selectedSize.size_id,
                      selectedSize.id
                    );

                    if (result.success) {
                      setShowCartToast(true);
                    } else {
                      setFavoriteToast({
                        message:
                          result.message || "Error al agregar al carrito",
                        show: true,
                        success: false,
                      });
                    }
                  }
                }}
                disabled={!selectedSize}
              >
                Añadir al carrito
              </button>
            </div>
          </Col>
        </Row>
        {(product?.colors[selectedColorIndex]?.description ||
          product?.brand) && (
          <div className="description-section">
            <Button
              className="description-toggle"
              onClick={() => setIsDescriptionOpen(!isDescriptionOpen)}
            >
              <div className="description-text">
                <span>Descripción</span>
                <FontAwesomeIcon
                  icon={isDescriptionOpen ? faChevronUp : faChevronDown}
                />
              </div>
            </Button>
            {isDescriptionOpen && (
              <div className="description-content">
                {product?.colors[selectedColorIndex]?.description && (
                  <>
                    <p className="title">Descripción del producto:</p>
                    <p>{product?.colors[selectedColorIndex]?.description}</p>
                  </>
                )}
                {product.brand && (
                  <>
                    <p className="title">Marca del producto:</p>
                    <p>{product.brand}</p>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className="d-flex align-items-center justify-content-center"
        style={{ margin: "0 0 20px 0" }}
      >
        {!isMobile && (
          <div
            style={{
              height: "1px",
              background: primaryColor,
              flex: 1,
              maxWidth: "32%",
              boxShadow: "2px 4px 3px rgba(0,0,0,0.6)",
            }}
          />
        )}
        <h1
          style={{
            color: "black",
            fontWeight: "bold",
            fontSize: isMobile ? "20px" : "44px",
            letterSpacing: "3%",
            display: "flex",
            alignItems: "center",
            marginLeft: isMobile ? "2rem" : "3rem",
            marginRight: isMobile ? "2rem" : "3rem",
          }}
        >
          Productos relacionados
        </h1>
        {!isMobile && (
          <div
            style={{
              height: "1px",
              background: primaryColor,
              flex: 1,
              maxWidth: "32%",
              boxShadow: "2px 4px 3px rgba(0,0,0,0.6)",
            }}
          />
        )}
      </div>

      <RelatedProducts
        categoryIds={product?.categories.map((cat) => cat.id).join(",")}
      />

      {/* Sección de Reseñas */}
      {reviews.length > 0 && (
        <>
          <div
            ref={reviewsRef}
            className="d-flex align-items-center justify-content-center"
            style={{ margin: "40px 0 20px 0" }}
          >
            {!isMobile && (
              <div
                style={{
                  height: "1px",
                  background: primaryColor,
                  flex: 1,
                  maxWidth: "32%",
                  boxShadow: "2px 4px 3px rgba(0,0,0,0.6)",
                }}
              />
            )}
            <h1
              style={{
                color: "black",
                fontWeight: "bold",
                fontSize: isMobile ? "20px" : "44px",
                letterSpacing: "3%",
                display: "flex",
                alignItems: "center",
                marginLeft: isMobile ? "2rem" : "3rem",
                marginRight: isMobile ? "2rem" : "3rem",
              }}
            >
              Reseñas
            </h1>
            {!isMobile && (
              <div
                style={{
                  height: "1px",
                  background: primaryColor,
                  flex: 1,
                  maxWidth: "32%",
                  boxShadow: "2px 4px 3px rgba(0,0,0,0.6)",
                }}
              />
            )}
          </div>
          <ProductReviews reviews={reviews} productName={product.name} />
        </>
      )}
      <div
        className="d-flex align-items-center justify-content-center"
        style={{ margin: "10px 0 0 0" }}
      >
        {!isMobile && (
          <div
            style={{
              height: "1px",
              background: primaryColor,
              flex: 1,
              maxWidth: "32%",
              boxShadow: "2px 4px 3px rgba(0,0,0,0.6)",
            }}
          />
        )}
        <h1
          style={{
            color: "black",
            fontWeight: "bold",
            fontSize: isMobile ? "40px" : "64px",
            letterSpacing: "3%",
            display: "flex",
            alignItems: "center",
            marginLeft: isMobile ? "3rem" : "4rem",
          }}
        >
          NOVEDADES
        </h1>
        <img
          src={stars}
          alt="estrella"
          style={{
            width: isMobile ? "3rem" : "4rem",
            height: isMobile ? "3rem" : "4rem",
            verticalAlign: "middle",
          }}
        />
        {!isMobile && (
          <div
            style={{
              height: "1px",
              background: primaryColor,
              flex: 1,
              maxWidth: "32%",
              boxShadow: "2px 4px 3px rgba(0,0,0,0.6)",
            }}
          />
        )}
      </div>
      <NewestProducts from="productDetails" />
      <Footer />
      <CustomToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message="¡Link copiado, listo para compartir!"
        width="330px"
      />
      <CustomToast
        show={showCartToast}
        onClose={() => setShowCartToast(false)}
        message="¡Añadido al carrito!"
        width="220px"
      />
      <CustomToast
        message={favoriteToast.message}
        onClose={() => {
          setFavoriteToast({ message: "", show: false, success: true });
        }}
        show={favoriteToast.show}
        success={favoriteToast.success}
      />
      <ImageModal
        isOpen={isImageModalOpen}
        onClose={handleCloseImageModal}
        images={product?.colors[selectedColorIndex]?.images}
        selectedIndex={selectedImageIndex}
        onSelectImage={setSelectedImageIndex}
        isReadOnly={true}
      />
    </>
  );
};

export default ProductDetailsClients;
